import React, { useRef, useState, useEffect, memo } from 'react';
import * as THREE from 'three';
import { useModelLoader } from '../../hooks/useModelLoader';
import { useCanvasContext } from '../../contexts/CanvasContext';
import { usePreloading } from '../../contexts/SiteContext';
import sceneConfig from '../../config/sceneConfig';
import { logger } from '../../utils/logger';
import { isNewsInteractiveElement, setCanvasPointer } from '../../utils/interactive3DHelpers';

// Texture positioning configuration for newspaper thumbnail
// All values are in pixels relative to the base texture
const NEWSPAPER_TEXTURE_CONFIG = {
  // Base texture dimensions (the original texture on the mesh)
  baseTextureWidth: 2048,  // Width of the base texture in pixels
  baseTextureHeight: 2048, // Height of the base texture in pixels
  
  // Newspaper image position on the base texture (in pixels)
  positionX: 1592, // X position in pixels (left edge of the image)
  positionY: 645,  // Y position in pixels (top edge of the image, note: Three.js UV Y=0 is bottom)
  
  // Newspaper image dimensions (in pixels)
  imageWidth: 455,  // Width of the newspaper thumbnail image
  imageHeight: 297, // Height of the newspaper thumbnail image
};

const NewsScene = ({ onSceneClick, offsetPosition }) => {
  const { gltf, isLoading, error } = useModelLoader('/models/news_stand.glb', 'news-scene');
  const { isPreloading } = usePreloading();
  const { registerSceneObject, getSceneObject, setModelLoaded, readyToPosition, openNewspaperModal } = useCanvasContext();
  const sceneRef = useRef();
  const [sceneObject, setSceneObject] = useState(null);
  const [isModelProcessed, setIsModelProcessed] = useState(false);
  const newspaperTextureRef = useRef(null);

  // Model loading and processing
  useEffect(() => {
    if (gltf && gltf.scene && !isModelProcessed) {
      // Check if we already have processed scene in the global context
      const existingSceneObject = getSceneObject('news-scene');
      
      if (existingSceneObject) {
        logger.debug('Using cached news scene from global context');
        setSceneObject(existingSceneObject);
        setIsModelProcessed(true);
        return;
      }

      // Process the model for the first time
      
      // Adjust scale and position for the new model
      // Get bounding box to understand the model size
      new THREE.Box3().setFromObject(gltf.scene);
      
      // Use configuration for news positioning
      const newsConfig = sceneConfig.modelPositions.news;
      gltf.scene.scale.setScalar(newsConfig.scale);
      
      // Use loading position during preload, final position with offset otherwise
      // All models use the same preload position for consistent viewing
      // During preload, ignore offsetPosition to keep objects at loading position
      const position = isPreloading ? [0, -65, -150] : (offsetPosition || newsConfig.position);
      gltf.scene.position.set(...position);
      
      // During preload, use neutral rotation (0) so news stand faces camera for better material compilation
      // After preload, use the configured rotation
      const rotation = isPreloading ? [0, 0, 0] : newsConfig.rotation;
      gltf.scene.rotation.set(...rotation);
      
      
        
      // Defer heavy mesh processing to prevent blocking the main thread
      const processMeshes = () => {
        gltf.scene.traverse((child) => {
          if (child.isMesh) {
            try {
              // Ensure material properties are set while preserving transparency
              if (child.material) {
                child.material.needsUpdate = true;
                
                // Log material properties for debugging
                
                // Only set transparency to false if it wasn't already transparent
                if (!child.material.transparent) {
                  child.material.transparent = false;
                  child.material.opacity = 1.0;
                } else {
                }
              }
              
              // Enable shadow casting and receiving (will be controlled by showShadows setting)
              child.castShadow = true;
              child.receiveShadow = true;
              
              // Add click handlers for debug
              child.userData.originalName = child.name;
              child.userData.originalPosition = child.position.clone();
              child.userData.isClickable = true;
              
              // Mark newsbox-blue specifically as interactive for cursor pointer
              const normalizedName = child.name.toLowerCase();
              const normalizedOrig = (child.userData.originalName || '').toLowerCase();
              if (normalizedName.includes('newsbox-blue') || normalizedOrig.includes('newsbox-blue') ||
                  normalizedName === 'newsboxblue' || normalizedOrig === 'newsboxblue' ||
                  normalizedName === 'newsbox_blue' || normalizedOrig === 'newsbox_blue') {
                child.userData.isNewsInteractive = true;
              }
            } catch (error) {
              console.warn('Error processing mesh:', child.name, error);
            }
          }
        });
      };
      
      // Defer mesh processing to prevent blocking the main thread
      setTimeout(processMeshes, 0);
      
      // Register scene in global context for reuse
      registerSceneObject('news-scene', gltf.scene);
      
      // Set reference for current use
      setSceneObject(gltf.scene);
      
      setIsModelProcessed(true);
      
      // Don't signal loaded yet - wait for readyToPosition signal
    }
  }, [gltf, registerSceneObject, getSceneObject, isModelProcessed, isPreloading, setModelLoaded, offsetPosition]);

  // Move to final position when preloader signals ready
  useEffect(() => {
    if (sceneObject && readyToPosition) {
      const newsConfig = sceneConfig.modelPositions.news;
      const targetPos = new THREE.Vector3(...(offsetPosition || newsConfig.position));
      const targetRot = new THREE.Euler(...newsConfig.rotation);
      sceneObject.position.copy(targetPos);
      sceneObject.rotation.copy(targetRot);
      
      // Signal that news model is loaded and positioned (this also resolves the promise)
      setModelLoaded('news', true);
    }
  }, [sceneObject, readyToPosition, setModelLoaded, offsetPosition]);

  // Update position when offset changes (after initial load)
  // Don't update during preload - keep objects at loading position
  useEffect(() => {
    if (sceneObject && readyToPosition && offsetPosition && !isPreloading) {
      const targetPos = new THREE.Vector3(...offsetPosition);
      sceneObject.position.copy(targetPos);
    }
  }, [sceneObject, readyToPosition, offsetPosition, isPreloading]);

  // Load and apply newspaper thumbnail texture after model is processed
  useEffect(() => {
    if (!sceneObject || !isModelProcessed) {
      return;
    }

    // Check if texture is already applied to avoid reloading
    let textureAlreadyApplied = false;
    sceneObject.traverse((child) => {
      if (child.isMesh && child.material) {
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        materials.forEach((material) => {
          if (material?.map?.image?.src?.includes('newspaper')) {
            textureAlreadyApplied = true;
            newspaperTextureRef.current = material.map;
          }
        });
      }
    });

    // If texture is already applied, we're done
    if (textureAlreadyApplied) {
      return;
    }

    // Only load texture once
    if (newspaperTextureRef.current) {
      return;
    }

    const loadNewspaperTexture = () => {
      // First, find the newsbox-blue mesh and get the original texture
      let targetMesh = null;
      let originalMaterial = null;
      
      sceneObject.traverse((child) => {
        if (child.isMesh && !targetMesh) {
          const normalizedName = (child.name || '').toLowerCase();
          const normalizedOrig = (child.userData.originalName || '').toLowerCase();
          
          const isNewsboxBlue = 
            normalizedName === 'newsbox-blue' || 
            normalizedOrig === 'newsbox-blue' ||
            normalizedName.includes('newsbox-blue') ||
            normalizedOrig.includes('newsbox-blue');
          
          if (isNewsboxBlue && child.material) {
            targetMesh = child;
            originalMaterial = Array.isArray(child.material) ? child.material[0] : child.material;
          }
        }
      });
      
      if (!targetMesh || !originalMaterial) {
        logger.debug('⚠️ Could not find newsbox-blue mesh. Available meshes:', 
          Array.from(new Set(
            (() => {
              const names = [];
              sceneObject.traverse((child) => {
                if (child.isMesh) {
                  names.push(child.name || 'unnamed');
                }
              });
              return names;
            })()
          ))
        );
        return;
      }
      
      // Get the original texture
      const originalTexture = originalMaterial.map;
      if (!originalTexture) {
        console.warn('⚠️ No original texture found on newsbox-blue material');
        return;
      }
      
      // Wait for original texture to load if it's not ready
      const processTextures = () => {
        if (!originalTexture.image || originalTexture.image.width === 0) {
          // Wait a bit and try again
          setTimeout(processTextures, 100);
          return;
        }
        
        const textureLoader = new THREE.TextureLoader();
        
        // Load the newspaper thumbnail
        textureLoader.load(
          '/images/thumbnails/newspaper.jpg',
          (newspaperTexture) => {
            const config = NEWSPAPER_TEXTURE_CONFIG;
            
            
            // Helper function to apply the composite texture
            const applyCompositeTexture = (canvas, mesh, material, config) => {
              // Create a new texture from the canvas
              const compositeTexture = new THREE.CanvasTexture(canvas);
              
              // Preserve original texture properties for color accuracy
              compositeTexture.colorSpace = originalTexture.colorSpace || THREE.SRGBColorSpace;
              compositeTexture.format = originalTexture.format || THREE.RGBAFormat;
              compositeTexture.encoding = originalTexture.encoding; // For older Three.js versions
              
              compositeTexture.wrapS = originalTexture.wrapS || THREE.RepeatWrapping;
              compositeTexture.wrapT = originalTexture.wrapT || THREE.RepeatWrapping;
              compositeTexture.minFilter = originalTexture.minFilter || THREE.LinearFilter;
              compositeTexture.magFilter = originalTexture.magFilter || THREE.LinearFilter;
              compositeTexture.flipY = false;
              compositeTexture.needsUpdate = true;
              
              newspaperTextureRef.current = compositeTexture;
              
              // Apply the composite texture to the material
              const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
              materials.forEach((mat) => {
                if (mat) {
                  mat.map = compositeTexture;
                  mat.needsUpdate = true;
                }
              });
              
              logger.debug(`✅ Applied composite texture with newspaper thumbnail to mesh: ${mesh.name}`, {
                pixelPosition: { x: config.positionX, y: config.positionY },
                imageSize: { width: config.imageWidth, height: config.imageHeight }
              });
            };
            
            // Create a canvas to composite the textures
            // Use actual texture dimensions if available, otherwise use config
            const textureWidth = originalTexture.image?.width || config.baseTextureWidth;
            const textureHeight = originalTexture.image?.height || config.baseTextureHeight;
            
            // Calculate scale factor if texture size differs from config
            const scaleX = textureWidth / config.baseTextureWidth;
            const scaleY = textureHeight / config.baseTextureHeight;
            
            const canvas = document.createElement('canvas');
            canvas.width = textureWidth;
            canvas.height = textureHeight;
            const ctx = canvas.getContext('2d');
            
            // Preserve image quality settings
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            
            // Draw the original texture at its actual size (no scaling to preserve colors)
            ctx.drawImage(originalTexture.image, 0, 0);
            
            // Wait for newspaper texture image to be ready
            const processNewspaper = () => {
              if (!newspaperTexture.image || newspaperTexture.image.width === 0) {
                // Wait a bit and try again
                setTimeout(processNewspaper, 50);
                return;
              }
              
              // Scale pixel coordinates to match actual texture size
              const scaledPositionX = config.positionX * scaleX;
              const scaledPositionY = config.positionY * scaleY;
              const scaledImageWidth = config.imageWidth * scaleX;
              const scaledImageHeight = config.imageHeight * scaleY;
              
              // Draw the newspaper on top of the original texture at scaled coordinates
              ctx.drawImage(
                newspaperTexture.image,
                scaledPositionX,
                scaledPositionY,
                scaledImageWidth,
                scaledImageHeight
              );
              
              // Apply the composite texture
              applyCompositeTexture(canvas, targetMesh, originalMaterial, config);
            };
            
            processNewspaper();
          },
          undefined,
          (error) => {
            console.warn('Failed to load newspaper thumbnail texture:', error);
          }
        );
      };
      
      // Start processing
      processTextures();
    };

    // Load texture asynchronously after a short delay to not block model processing
    setTimeout(loadNewspaperTexture, 100);
  }, [sceneObject, isModelProcessed]);

  // Show loading state
  if (isLoading) {
    return null; // Don't render anything while loading
  }

  if (error) {
    console.error('Error loading news scene model:', error);
    return null;
  }
  
  return (
    <>
      {/* Main spotlight positioned high above, facing down with large coverage */}
      <spotLight
        position={[0, 80, 0]} // Much higher above the camera
        target-position={[0, -60, 140]} // Pointing down at news stand area
        intensity={2}
        angle={Math.PI / 1.2} // ~150 degree cone for very large coverage
        penumbra={0.3}
        distance={500}
        decay={0.2}
        color="#ffffff"
        castShadow={true}
      />
      
      {/* Secondary spotlight from higher side position */}
      <spotLight
        position={[40, 60, 20]} // Higher and further to the side
        target-position={[0, -60, 140]} // Pointing down at news stand
        intensity={1.5}
        angle={Math.PI / 1.5} // ~120 degree cone for wide coverage
        penumbra={0.4}
        distance={400}
        decay={0.3}
        color="#f8f8f8"
        castShadow={false}
      />
      
      {/* Point light at camera position with large radius */}
      <pointLight
        position={[0, 0, 0]} // At camera position
        intensity={3}
        distance={500}
        decay={0.2}
        color="#ffffff"
      />
      
      {/* Render the news scene */}
      {sceneObject && (
        <primitive 
          ref={sceneRef} 
          object={sceneObject}
          onPointerOver={(event) => {
            // Use helper function to check if the object is interactive
            if (isNewsInteractiveElement(event.object)) {
              setCanvasPointer(true);
              event.stopPropagation();
            }
          }}
          onPointerOut={(event) => {
            if (isNewsInteractiveElement(event.object)) {
              setCanvasPointer(false);
            }
          }}
          onClick={(event) => {
            logger.debug('=== NEWS SCENE CLICKED ===');
            logger.debug('News clicked at:', event.point);
            logger.debug('Clicked object:', event.object.name);
            logger.debug('==========================');
            
            // Check if the clicked object is the newsbox-blue mesh
            if (event.object.name === 'newsbox-blue' || 
                event.object.userData.originalName === 'newsbox-blue') {
              logger.debug('Newsbox-blue mesh clicked - opening newspaper modal');
              openNewspaperModal();
            } else if (onSceneClick) {
              onSceneClick();
            }
          }}
        />
      )}
    </>
  );
};

export default memo(NewsScene, (prevProps, nextProps) => {
  // Only re-render if the callback functions actually changed
  return prevProps.onSceneClick === nextProps.onSceneClick;
});
